import classNames from 'classnames';
import _ from 'underscore';
import PropTypes from 'prop-types';
import React from 'react';
import { ActionButton, IconButton, FontIcon } from '@fluentui/react';
import { CampaignType, StatusWaterfallFilterOptions } from '@bingads-webui-campaign-react/shell-ui-common';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { QueryParam, withScope } from '@bingads-webui-react/scope-service';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { NodeOperations } from '@bingads-webui-react/tree-view';
import { Mode } from '@bingads-webui-react/app-layout-container';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { StyledCollapsibleSideBar } from './components/collapsible-side-bar/collapsible-side-bar';
import { CampaignTreeNodeChildRenderer } from './components/campaign-main-menu/main-menu-tree/campaign-tree-node';
import { createPlaceholderNode } from './components/campaign-main-menu/main-menu-tree/placeholder-node-generator';
import MenuItemList from './components/campaign-main-menu/menu/menu-item-list';
import NavigationBarTree from './components/campaign-main-menu/main-menu-tree/navigation-bar-tree';
import { StyledWaterfallFilter } from './components/campaign-main-menu/main-menu-filter/waterfall-filter';
import { getCampaignTypesProps } from './helpers/campaign-type-config';
import { getMainMenuStyles } from './styles/main-menu-styles';
import { withResponsiveMainMenu } from './withResponsiveMainMenu';

export const SINGLE_CAMPAIGN_VIEW_AD_GROUP_COUNT_THRESHOLD = 100;

class CampaignMainMenuView extends React.PureComponent {
  constructor(props) {
    super(props);

    this.shouldReportCampaignTypeChange = false;

    this.mainMenuScenario = props.instrumentation.mainMenuScenario.create();
    this.leftNavigationActivity = this.mainMenuScenario.leftNavigationActivity.create();
    this.hideCampaignListActivity = this.mainMenuScenario.hideCampaignListActivity.create();

    this.state = {
      expandedTreeNodesIdHash: {},
      showSingleNodeTreeIfAdGroupCountAboveThrehold: true,
      previousCampaignId: props.selectedCampaignId,
      showFallbackId: null,
    };
  }

  static getDerivedStateFromProps(props, state) {
    const {
      selectedCampaignId: campaignId,
      selectedAdGroupId: adGroupId,
    } = props;

    const { expandedTreeNodesIdHash } = state;

    // Ensure Campaign of selected AdGroup is expanded.
    let patch = {};
    if (adGroupId && campaignId) {
      patch = { [campaignId.toString()]: true };
    }

    const newExpandedTreeNodesIdHash = { ...expandedTreeNodesIdHash, ...patch };

    if (campaignId !== state.previousCampaignId) {
      return {
        showSingleNodeTreeIfAdGroupCountAboveThrehold: true,
        previousCampaignId: campaignId,
        expandedTreeNodesIdHash: newExpandedTreeNodesIdHash,
      };
    }

    return {
      expandedTreeNodesIdHash: newExpandedTreeNodesIdHash,
    };
  }

  componentDidMount() {
    this.checkIfHasCampaign();
    this.reportCampaignTypeChange();

    if (_.result(this.props.serverContext, ['Permissions', 'IsLeftNavigationHideButtonEnabled'])) {
      this.leftNavigationActivity.trace('Left navigation hide button is rendered');
    }

    if (_.result(this.props.serverContext, ['Permissions', 'IsHideCampaignListButtonEnabled'])) {
      this.hideCampaignListActivity.trace('Hide campaign list button is rendered');
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.hotKey === 'shift+w' && prevProps.count !== this.props.count) {
      this.onToggle();
    } else if (this.props.hotKey === 'shift+a' && prevProps.count !== this.props.count) {
      this.props.onCampaignTypeChange({ campaignType: CampaignType.All });
    } else if (this.props.hotKey === 'shift+s' && prevProps.count !== this.props.count) {
      this.props.onCampaignTypeChange({ campaignType: CampaignType.Default });
    } else if (this.props.hotKey === 'shift+p' && prevProps.count !== this.props.count) {
      this.props.onCampaignTypeChange({ campaignType: CampaignType.Shopping });
    } else if (this.props.hotKey === 'shift+d' && prevProps.count !== this.props.count) {
      this.props.onCampaignTypeChange({ campaignType: CampaignType.Audience });
    } else if (this.props.hotKey === 'shift+l' && prevProps.count !== this.props.count) {
      this.props.onCampaignTypeChange({ campaignType: CampaignType.Hotel });
    } else if (this.props.hotKey === 'shift+t' && prevProps.count !== this.props.count) {
      if (this.existingCampaignTypes.indexOf(CampaignType.Smart) !== -1) {
        this.props.onCampaignTypeChange({ campaignType: CampaignType.Smart });
      } else {
        this.props.onCampaignTypeChange({ campaignType: CampaignType.All });
      }
    }

    this.checkIfHasCampaign();
    this.reportCampaignTypeChange();
  }

  onToggle = () => {
    this.props.onToggle();
  };

  onTreeNodeChange = (nodes, node) => {
    // Click on placeholder node can also trigger onTreeNodeChange which should be ignored
    if (node.isPlaceholderNode) {
      return;
    }

    // Only fire to adapter layer if selection changed but also skip own state changes
    // if adapter layer detected rejected navigation.
    // TODO: rework this solution as described in User Story https://msasg.visualstudio.com/Bing_Ads/_workitems/edit/5019951
    if (!node.isSelected && !this.props.onTreeNodeChange(node)) {
      return;
    }

    if (!node.parentId) {
      const { expandedTreeNodesIdHash } = this.state;
      const key = node.id.toString();

      const patch = {
        [key]: !expandedTreeNodesIdHash[key],
      };

      this.setState({
        expandedTreeNodesIdHash: { ...expandedTreeNodesIdHash, ...patch },
        showSingleNodeTreeIfAdGroupCountAboveThrehold: true,
      });
    }

    this.setState({
      showFallbackId: node.id,
    });
  };

  onCampaignTypeSelectionChange = node => () => {
    const {
      selectedCampaignId,
      selectedAdGroupId,
      selectedCampaignType,
    } = this.props;

    if (selectedCampaignId
      || selectedAdGroupId
      || selectedCampaignType !== node.campaignType) {
      this.props.onCampaignTypeChange(node);
    }
  };

  onBackToAllCampaignsClicked = () => {
    const { expandedTreeNodesIdHash } = this.state;
    const { selectedCampaignId } = this.props;
    const key = selectedCampaignId.toString();
    const patch = {
      [key]: false,
    };

    this.setState({
      showSingleNodeTreeIfAdGroupCountAboveThrehold: false,
      expandedTreeNodesIdHash: { ...expandedTreeNodesIdHash, patch },
    });
  };

  getCampaignTypes() {
    const { campaigns } = this.props;
    return _.chain(campaigns)
      .map(campaign => campaign.campaignType)
      .uniq()
      .value();
  }

  getCampaignsToShow(finalSelectedCampaignType) {
    const {
      campaigns, statusWaterfallFilter, selectedCampaignId,
    } = this.props;

    const showAllStatus =
      statusWaterfallFilter.campaign === StatusWaterfallFilterOptions.All;

    const campaignTypePredicate = campaign =>
      finalSelectedCampaignType === CampaignType.All
      || finalSelectedCampaignType === campaign.campaignType;
    const waterFallFilterPredicate = campaign =>
      (showAllStatus || campaign.status === 'Active') &&
      campaign.rawStatus !== 'InProgress';

    const campaignsInCurrentCampaignType = campaigns.filter(campaign => campaignTypePredicate(campaign));
    const isSelectedCampaignMatchCurrentCampaignType = selectedCampaignId ? !!_.findWhere(campaignsInCurrentCampaignType, { id: selectedCampaignId }) : true;

    return {
      campaignsToShow: campaigns.filter(campaign =>
        campaignTypePredicate(campaign) && waterFallFilterPredicate(campaign)),
      isSelectedCampaignMatchCurrentCampaignType,
    };
  }

  getTreeNodesProps(finalSelectedCampaignType) {
    const {
      selectedCampaignId: currentCampaignId,
      selectedAdGroupId: currentAdGroupId,
      i18n,
      statusWaterfallFilter,
      isLoadingData,
    } = this.props;
    const {
      showFallbackId,
      expandedTreeNodesIdHash,
      showSingleNodeTreeIfAdGroupCountAboveThrehold,
    } = this.state;

    let adGroupCountAboveThreshold = false;
    let campaignExceedAdGroupCountThreshold;

    const ariaLabelMapping = {
      Active: i18n.getString(_TL_('Enabled - {{entityName}}')),
      Paused: i18n.getString(_TL_('Paused - {{entityName}}')),
    };

    const {
      campaignsToShow,
      isSelectedCampaignMatchCurrentCampaignType,
    } = this.getCampaignsToShow(finalSelectedCampaignType);

    const campaignPlaceholderName = isLoadingData
      ? i18n.getString(_TL_('Loading ...'))
      : i18n.getString(_TL_('No matching campaigns'));

    const campaignsInTree = campaignsToShow.length === 0
      ? [createPlaceholderNode('Campaign', campaignPlaceholderName, isLoadingData)]
      : campaignsToShow.map((campaign) => {
        let adGroups = [];
        let adGroupSelected = false;

        const showAllAdGroups = statusWaterfallFilter.adGroup === StatusWaterfallFilterOptions.All;

        if (_.isArray(campaign.children)) {
          adGroups = campaign.children.map((adGroup) => {
            if (currentAdGroupId === adGroup.id) {
              adGroupSelected = true;
            }

            return {
              ...adGroup,
              isSelected: currentAdGroupId === adGroup.id,
              ariaLabel: ariaLabelMapping[adGroup.status],
            };
          }).filter(adGroup => showAllAdGroups || adGroup.status === 'Active');
        }

        const isSelectedCampaign = currentCampaignId === campaign.id;

        if (isSelectedCampaign
          && adGroups.length >= SINGLE_CAMPAIGN_VIEW_AD_GROUP_COUNT_THRESHOLD) {
          adGroupCountAboveThreshold = true;

          // Always expand campaign node in single-node tree and collapse it in multi-nodes tree.
          campaignExceedAdGroupCountThreshold = {
            ...campaign,
            isSelected: (!adGroupSelected && isSelectedCampaign),
            children: adGroups.slice(),
            iconHidden: true,
            state: { expanded: true },
            ariaLabel: ariaLabelMapping[campaign.status],
          };

          return { ...campaignExceedAdGroupCountThreshold, state: { expanded: false } };
        }

        // For selected campaign without any adGroup, give it an empty placeholder node.
        // Do not creat placeholder node for smart campaigns and performance max campaigns
        // Smart campaigns, app campaigns, and performance max campaigns do not contains ad-groups.
        if (_.isEmpty(adGroups) && (campaign.campaignType !== CampaignType.Smart)
          && (campaign.campaignType !== CampaignType.PerformanceMax) && (campaign.campaignType !== CampaignType.App)) {
          const isLoadingAdGroups = isLoadingData && isSelectedCampaign;

          const adGroupPlaceholderName = isLoadingAdGroups
            ? i18n.getString(_TL_('Loading ...'))
            : i18n.getString(_TL_('No ad groups'));

          adGroups = [createPlaceholderNode('AdGroup', adGroupPlaceholderName, isLoadingAdGroups)];
        }

        // If one AdGroup of this Campaign is selected, then campaign should in un-selected state.
        return {
          ...campaign,
          isSelected: (!adGroupSelected && isSelectedCampaign),
          children: adGroups,
          iconHidden: true,
          state: {
            expanded: adGroups.length >= SINGLE_CAMPAIGN_VIEW_AD_GROUP_COUNT_THRESHOLD ?
              false :
              expandedTreeNodesIdHash[campaign.id.toString()],
            showFallback: campaign.id === showFallbackId,
          },
          hideFallback: () => this.setState({ showFallbackId: null }),
          ariaLabel: ariaLabelMapping[campaign.status],
        };
      });

    const willShowSingleNodeTree = showSingleNodeTreeIfAdGroupCountAboveThrehold &&
      adGroupCountAboveThreshold;
    const singleCampaign = willShowSingleNodeTree ? [campaignExceedAdGroupCountThreshold] : [];

    return {
      campaignsInTree,
      willShowSingleNodeTree,
      singleCampaign,
      isSelectedCampaignMatchCurrentCampaignType,
    };
  }

  checkIfHasCampaign() {
    const { campaigns } = this.props;
    if (campaigns.length > 0) {
      // if (window.survey && window.survey.setSurveyHasCampaign) {
      //   window.survey.setSurveyHasCampaign();
      // }
    }
  }

  reportCampaignTypeChange = () => {
    if (this.shouldReportCampaignTypeChange) {
      this.shouldReportCampaignTypeChange = false;
      this.props.onCampaignTypeChange({ campaignType: CampaignType.All }, false);
    }
  };

  renderDivider() {
    const { classes } = this.props;
    return (<div className={classNames('toggle-menu-item-divider', classes.divider)} />);
  }

  render() {
    const {
      serverContext,
      classes,
      selectedCampaignId,
      selectedAdGroupId,
      selectedCampaignType,
      mode,
      i18n,
      statusWaterfallFilter,
      onWaterfallFilterChange,
      isLoadingData,
      isPanelMode,
    } = this.props;

    const isLongCampaignAndAdgroupNameEnabled = _.result(serverContext, ['Permissions', 'IsLongCampaignAndAdgroupNameEnabled']);

    this.existingCampaignTypes = this.getCampaignTypes();

    // Fallback to 'All campaigns' when selected CampaignType is not in existing Campaign types.
    const isSelectedCampaignTypeAvailable
      = (selectedCampaignType === CampaignType.All)
      || (this.existingCampaignTypes.indexOf(selectedCampaignType) >= 0);

    let finalSelectedCampaignType = selectedCampaignType;

    const {
      campaignsInTree, willShowSingleNodeTree, singleCampaign, isSelectedCampaignMatchCurrentCampaignType,
    } = this.getTreeNodesProps(finalSelectedCampaignType);

    // Not able to decide if CampaignType filter is available or not until data loading is finished.
    if ((!isSelectedCampaignTypeAvailable || !isSelectedCampaignMatchCurrentCampaignType) && !isLoadingData) {
      finalSelectedCampaignType = CampaignType.All;
      this.shouldReportCampaignTypeChange = true;
    }

    const campaignTypesConfig = getCampaignTypesProps(
      this.existingCampaignTypes,
      finalSelectedCampaignType,
      i18n
    );

    const campaignsTreeUpdateTypeHandlers = {
      [NodeOperations.EXPANDED]: this.onTreeNodeChange,
      [NodeOperations.COLLAPSED]: this.onTreeNodeChange,
    };

    const treeViewContainerClassName = classNames([
      classes.mainMenuContainer,
      mode === Mode.Expanded || isPanelMode ? 'ui-next-tree-view' : [classes.hidden, 'ui-next-hidden-tree-view'],
      isLongCampaignAndAdgroupNameEnabled ? classes.longCampaignAndAdgroupName : '',
    ]);

    const scrollToId = selectedAdGroupId || selectedCampaignId || null;

    const isExpanded = isPanelMode || mode === Mode.Expanded;

    const {
      scope: {
        values: {
          cid, aid, campaignId, adGroupId,
        },
      },
    } = this.props;

    const scopeObject = {
      cid, aid, campaignId, adGroupId,
    };

    const onHideCampaignListButtonClick = () => {
      this.hideCampaignListActivity.trace({
        ...scopeObject,
      });
      setClarityTag({ HideCampaignListButtonClicked: 'true' });
      this.props.onToggle();
    };

    const onLeftNavigationButtonClick = () => {
      this.leftNavigationActivity.trace({
        ...scopeObject,
        isExpanded,
      });
      setClarityTag({ LeftNavigationButtonClicked: isExpanded ? 'expanded' : 'collapsed' });
      this.props.onToggle();
    };

    return (
      <StyledCollapsibleSideBar
        mode={mode}
        isPanelMode={isPanelMode}
        i18n={i18n}
      >
        <MenuItemList
          isExpanded={isExpanded}
          items={campaignTypesConfig}
          onMenuItemClick={this.onCampaignTypeSelectionChange}
        />

        {this.renderDivider()}

        <StyledWaterfallFilter
          i18n={i18n}
          onChange={onWaterfallFilterChange}
          selected={statusWaterfallFilter}
          isExpanded={isExpanded}
        />

        <div className={treeViewContainerClassName}>
          <NavigationBarTree
            ariaLabel={i18n.getString(_TL_('Campaign Main Menu Tree Grid'))}
            treeNodes={
              willShowSingleNodeTree
                ? singleCampaign
                : campaignsInTree
            }
            updateTypeHandlers={campaignsTreeUpdateTypeHandlers}
            nodeChildRenderer={CampaignTreeNodeChildRenderer}
            showBackMenuItem={willShowSingleNodeTree}
            backMenuItemText={i18n.getString(_TL_('Back to campaigns'))}
            onBackClicked={this.onBackToAllCampaignsClicked}
            scrollToId={scrollToId}
            perfMarker={this.props.perfMarker}
            shouldClearCellMeasurerCache={isLongCampaignAndAdgroupNameEnabled}
          />
        </div>

        {_.result(serverContext, ['Permissions', 'IsHideCampaignListButtonEnabled']) && isExpanded && this.renderDivider()}

        {_.result(serverContext, ['Permissions', 'IsHideCampaignListButtonEnabled']) && isExpanded &&
          <ActionButton
            className={classNames('hide-campaign-list-button', classes.hideCamapignListButton)}
            onClick={onHideCampaignListButtonClick}
          >
            {i18n.getString(_TL_('Hide campaign list'))}
            <FontIcon
              className={classNames('hide-campaign-list-icon', classes.hideCamapignListIcon)}
              aria-label={i18n.getString(_TL_('Hide campaign list'))}
              iconName="PaddingLeft"
            />
          </ActionButton>
        }

        {_.result(serverContext, ['Permissions', 'IsLeftNavigationHideButtonEnabled']) &&
          <IconButton
            iconProps={{ iconName: isExpanded ? 'ChevronLeftMed' : 'ChevronRightMed' }}
            className={classNames('left-navigation-button', isExpanded ? 'expanded' : 'collapsed', classes.leftNavigationButton)}
            ariaLabel={isExpanded ? i18n.getString(_TL_('Hide navigation menu')) : i18n.getString(_TL_('Show navigation menu'))}
            onClick={onLeftNavigationButtonClick}
          />
        }
      </StyledCollapsibleSideBar>
    );
  }
}

CampaignMainMenuView.propTypes = {
  serverContext: PropTypes.shape({
    Permissions: PropTypes.shape({}).isRequired,
  }),
  classes: PropTypes.shape(_.mapObject(
    getMainMenuStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  hotKey: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  campaigns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    children: PropTypes.arrayOf(PropTypes.shape({})),
    campaignType: PropTypes.number.isRequired,
  })).isRequired,
  mode: PropTypes.string.isRequired,
  selectedCampaignId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedAdGroupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedCampaignType: PropTypes.oneOf(_.values(CampaignType)).isRequired,
  onCampaignTypeChange: PropTypes.func.isRequired,
  onTreeNodeChange: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  statusWaterfallFilter: PropTypes.shape({
    campaign: PropTypes.oneOf([
      StatusWaterfallFilterOptions.AllEnabled,
      StatusWaterfallFilterOptions.All]).isRequired,
    adGroup: PropTypes.oneOf([
      StatusWaterfallFilterOptions.AllEnabled,
      StatusWaterfallFilterOptions.All]).isRequired,
  }).isRequired,
  onWaterfallFilterChange: PropTypes.func.isRequired,
  isLoadingData: PropTypes.bool.isRequired,
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }),
  isPanelMode: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  instrumentation: PropTypes.shape({
    mainMenuScenario: PropTypes.shape({
      create: PropTypes.func,
    }),
  }).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
};

CampaignMainMenuView.defaultProps = {
  serverContext: {
    Permissions: {},
  },
  selectedCampaignId: undefined,
  selectedAdGroupId: undefined,
  perfMarker: null,
};

export const StyledCampaignMainMenuView = _.compose(
  withServerContext,
  withInstrumentation,
  withScope,
  withResponsiveMainMenu,
  withI18nConsumer,
  WrappedComponent => withDefaultStyles(WrappedComponent, getMainMenuStyles)
)(CampaignMainMenuView);
